/**
 * The decision behind seeding the conversion-dedup store, without the storage.
 *
 * Apart and dependency-free on purpose. The store it feeds lives in
 * localStorage, and `conversion-dedup.ts` pulls in the script's shared state,
 * which touches `history` at import time. Testing that would mean rebuilding a
 * browser to check a merge, so the merge lives here instead.
 *
 * Two rules, and both of them are the whole point:
 *
 * Absent is not empty. No list means the server could not place this visitor
 * (no anchor cookie, or the lookup fell over). Treating that as "converted on
 * nothing" would wipe what the browser itself remembered.
 *
 * Merge, never replace. The server knows the visitor by their anchor cookie
 * only. Someone who cleared their cookies but kept localStorage is still the
 * same person, and their own history has to survive.
 */

/**
 * Returns the store to write, or null when there is nothing to write.
 *
 * Null and an unchanged object are different answers to the caller: null means
 * "do not touch localStorage at all", which keeps a page view that changes
 * nothing from doing a write on every load.
 */
export function mergeSeededRules(
  stored: Record<string, number>,
  ruleIds: ReadonlyArray<string> | undefined,
  now: number,
): Record<string, number> | null {
  if (!ruleIds || ruleIds.length === 0) return null;

  const merged: Record<string, number> = {};
  for (const key of Object.keys(stored)) merged[key] = stored[key];

  let added = 0;
  for (let i = 0; i < ruleIds.length; i++) {
    const ruleId = ruleIds[i];
    // A rule already stored keeps its own timestamp. That is when THIS browser
    // saw it, and overwriting it with "now" would push the only timing
    // information we have forward on every single page view.
    if (merged[ruleId]) continue;
    merged[ruleId] = now;
    added++;
  }

  if (added === 0) return null;
  return merged;
}
